import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faMagnifyingGlass,
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${month}/${day}/${d.getFullYear()}`;
};

function CandidateAthletesMobileList({
  athletes,
  stats,
  searchQuery,
  onSearchChange,
  onExport,
  loading,
  error,
  page,
  totalPages,
  totalResults,
  startRecord,
  endRecord,
  onPageChange,
}) {
  return (
    <div className="bg-white rounded-lg shadow-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-base font-semibold">Potential Athlete Signups</h1>
        <button
          onClick={onExport}
          className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 shadow-sm"
        >
          <FontAwesomeIcon icon={faDownload} />
          <span className="ml-1">Export</span>
        </button>
      </div>

      <div className="relative mb-2">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
        />
        <input
          type="text"
          placeholder="Search by Name/Email/School"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
        />
      </div>

      <div className="grid grid-cols-4 gap-1 py-4">
        <MobileStatItem label="TOTAL" value={stats.total} />
        <MobileStatItem label="THIS WEEK" value={stats.pastWeek} />
        <MobileStatItem label="NEW TODAY" value={stats.past24Hours} />
        <MobileStatItem label="PAST MONTH" value={stats.pastMonth} />
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading...</div>
      ) : error ? (
        <div className="text-center py-10 text-red-500">{error}</div>
      ) : athletes.length === 0 ? (
        <div className="text-center py-10 text-gray-400">No records found.</div>
      ) : (
        <>
          <div className="divide-y divide-gray-200">
            {athletes.map((a, i) => (
              <AthleteCard key={a.id ?? i} athlete={a} />
            ))}
          </div>

          {totalResults > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center mb-2">
                Showing {startRecord}–{endRecord} of {totalResults}
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => onPageChange((p) => p - 1)}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                </button>
                <span className="text-sm text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => onPageChange((p) => p + 1)}
                  disabled={page === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AthleteCard({ athlete: a }) {
  const [expanded, setExpanded] = useState(false);

  const subtitle = [a.city, a.state, a.expectedYearOfGraduation]
    .filter(Boolean)
    .join(" • ");

  return (
    <div className="py-2 px-3 border-gray-300 border rounded-md mb-3">
      <button
        className="w-full flex items-center justify-between text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div>
          <p className="font-semibold text-gray-800 text-base">
            {a.firstName} {a.lastName}
          </p>
          <p className="text-sm text-gray-700 mt-0.5">{subtitle}</p>
        </div>
        <FontAwesomeIcon
          icon={expanded ? faChevronUp : faChevronDown}
          className="text-gray-400 text-sm ml-2 shrink-0"
        />
      </button>

      {expanded && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
          <div className="col-span-2">
            <DetailRow label="Email" value={a.email} />
          </div>
          <DetailRow label="Event" value={a.eventName} />
          <DetailRow label="High School" value={a.highSchool} />
          <DetailRow label="Position" value={a.primaryPosition} />
          <DetailRow label="Athlete Phone" value={a.athletePhoneNumber} />
          <DetailRow label="Parent Phone" value={a.parentPhoneNumber} />
          <DetailRow label="Date" value={formatDate(a.createdAtUTC)} />
        </div>
      )}
    </div>
  );
}

function MobileStatItem({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase leading-tight">{label}</p>
      <p className="text-lg font-bold text-gray-800">{value}</p>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col pt-1">
      <label className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </label>
      <span className="mt-0 text-base text-gray-900">{value || "—"}</span>
    </div>
  );
}

export default CandidateAthletesMobileList;
